import React from 'react';
import { LazyAtlassianNavigation } from '@atlassian/jira-atlassian-navigation/src/async.tsx';
import { PLAN_CALENDAR } from '@atlassian/jira-capabilities/src/constants.tsx';
import { setCapability } from '@atlassian/jira-capabilities/src/services/active-capability-tracker/index.tsx';
import { ROUTE_GROUPS_SOFTWARE } from '@atlassian/jira-common-constants/src/spa-routes.tsx';
import { globalLayout } from '@atlassian/jira-global-layout/src/index.tsx';
import { AsyncHorizontalNavPlans } from '@atlassian/jira-horizontal-nav-plans/src/async.tsx';
import { MENU_ID, SIDEBAR_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import AsyncSidebarPlan from '@atlassian/jira-navigation-apps-sidebar-plan/src/async.tsx';
import { planLayout } from '@atlassian/jira-plan-layout/src/index.tsx';
import { createEntry } from '@atlassian/jira-route-entry/src/index.tsx';
import { composeLayouts } from '@atlassian/jira-route-layout/src/index.tsx';
import {
	advancedRoadmapsPlanPageInitialChecksResource,
	advancedRoadmapsReduxStoreStateResource,
	advancedRoadmapsLastVisitedProgramBoardIdResource,
} from '@atlassian/jira-router-resources-advanced-roadmaps-plan/src/services/index.tsx';
import { advancedRoadmapsSummaryDataResource } from '@atlassian/jira-router-resources-advanced-roadmaps-summary/src/services/summary-data/index.tsx';
import {
	getNavigationResources,
	getNavigationSidebarPlanResources,
} from '@atlassian/jira-router-resources-navigation/src/index.tsx';
import { arjEmbedRouteEntries } from '@atlassian/jira-router-routes-advanced-roadmaps-embed-entries/src/index.tsx';
import { softwareAdvancedRoadmapsGettingStartedRedirectRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwareAdvancedRoadmapsGettingStartedRedirectRoute.tsx';
import { softwarePlanCalendarRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanCalendarRoute.tsx';
import { softwarePlanDependenciesRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanDependenciesRoute.tsx';
import { softwarePlanReleasesRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanReleasesRoute.tsx';
import { softwarePlanSettingsAutoSchedulerRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsAutoSchedulerRoute.tsx';
import { softwarePlanSettingsCustomFieldsRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsCustomFieldsRoute.tsx';
import { softwarePlanSettingsExclusionRulesRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsExclusionRulesRoute.tsx';
import { softwarePlanSettingsFindYourIssueRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsFindYourIssueRoute.tsx';
import { softwarePlanSettingsIssueSourcesRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsIssueSourcesRoute.tsx';
import { softwarePlanSettingsPermissionsRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsPermissionsRoute.tsx';
import { softwarePlanSettingsPlanInfoRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsPlanInfoRoute.tsx';
import { softwarePlanSettingsProgramBoardsRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsProgramBoardsRoute.tsx';
import { softwarePlanSettingsRemovedIssuesRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsRemovedIssuesRoute.tsx';
import { softwarePlanSettingsSavedViewsRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsSavedViewsRoute.tsx';
import { softwarePlanSettingsScenariosRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsScenariosRoute.tsx';
import { softwarePlanSettingsSchedulingRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSettingsSchedulingRoute.tsx';
import { softwarePlanSummaryRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanSummaryRoute.tsx';
import { softwarePlanTeamsRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanTeamsRoute.tsx';
import { softwarePlanTimelineRoute } from '@atlassian/jira-router-routes-advanced-roadmaps-routes/src/softwarePlanTimelineRoute.tsx';
import { arjSettingsRouteEntries } from '@atlassian/jira-router-routes-advanced-roadmaps-settings-entries/src/index.tsx';
import { ScreenRedirect } from '@atlassian/jira-screen-redirect/src/ui/index.tsx';
import { CalendarSkeleton } from '@atlassian/jira-skeletons/src/ui/calendar/index.tsx';
import type { Route } from '@atlassian/react-resource-router';
import { planCalendarRouteEntry } from './ui/plan-calendar/index.tsx';
import PlanDependenciesReportPage, {
	LazyPlanDependenciesReport,
} from './ui/plan-dependencies-report/index.tsx';
import PlanReleasesPage, { LazyPlanReleases } from './ui/plan-releases/index.tsx';
import PlanSettingsAutoSchedulerPage, {
	LazyPlanSettingsAutoScheduler,
} from './ui/plan-settings-auto-scheduler/index.tsx';
import PlanSettingsCustomFieldsPage, {
	LazyPlanSettingsCustomFields,
} from './ui/plan-settings-custom-fields/index.tsx';
import PlanSettingsExclusionRulesPage, {
	LazyPlanSettingsExclusionRules,
} from './ui/plan-settings-exclusion-rules/index.tsx';
import PlanSettingsIssueFindYourIssuePage, {
	LazyPlanSettingsFindYourIssue,
} from './ui/plan-settings-find-your-issue/index.tsx';
import PlanSettingsIssueSourcesPage, {
	LazyPlanSettingsIssueSources,
} from './ui/plan-settings-issue-sources/index.tsx';
import PlanSettingsPermissionsPage, {
	LazyPlanSettingsPermissions,
} from './ui/plan-settings-permissions/index.tsx';
import PlanSettingsPlanInfoPage, {
	LazyPlanSettingsPlanInfo,
} from './ui/plan-settings-plan-info/index.tsx';
import PlanSettingsProgramBoardsPage, {
	LazyPlanSettingsProgramBoards,
} from './ui/plan-settings-program-boards/index.tsx';
import PlanSettingsRemovedIssuesPage, {
	LazyPlanSettingsRemovedIssues,
} from './ui/plan-settings-removed-issues/index.tsx';
import PlanSettingsSavedViewsPage, {
	LazyPlanSettingsSavedViews,
} from './ui/plan-settings-saved-views/index.tsx';
import PlanSettingsScenariosPage, {
	LazyPlanSettingsScenarios,
} from './ui/plan-settings-scenarios/index.tsx';
import PlanSettingsSchedulingPage, {
	LazyPlanSettingsScheduling,
} from './ui/plan-settings-scheduling/index.tsx';
import PlanSummaryPage, { LazyPlanSummary } from './ui/plan-summary/index.tsx';
import PlanTeamsPage, { LazyPlanTeams } from './ui/plan-teams/index.tsx';
import PlanTimelinePage, { LazyPlanTimeline } from './ui/plan-timeline/index.tsx';

export const planRouteEntries: Route[] = [
	...arjSettingsRouteEntries,
	...arjEmbedRouteEntries,
	createEntry(softwarePlanDependenciesRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanDependenciesReportPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
			advancedRoadmapsLastVisitedProgramBoardIdResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
			horizontal: AsyncHorizontalNavPlans,
			onlyShowHorizontalOnNav4: true,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanDependenciesReport],
	})),
	createEntry(softwarePlanSummaryRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		ufoName: 'software-advanced-roadmaps-summary',
		component: PlanSummaryPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsSummaryDataResource,
			advancedRoadmapsLastVisitedProgramBoardIdResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
			horizontal: AsyncHorizontalNavPlans,
			onlyShowHorizontalOnNav4: true,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSummary],
	})),
	createEntry(softwarePlanCalendarRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		ufoName: 'software-advanced-roadmaps-calendar',
		skeleton: () => <CalendarSkeleton />,

		entryPoint() {
			setCapability(PLAN_CALENDAR);
			return planCalendarRouteEntry;
		},

		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
			advancedRoadmapsLastVisitedProgramBoardIdResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
			horizontal: AsyncHorizontalNavPlans,
			onlyShowHorizontalOnNav4: true,
		},

		meta: {
			capability: PLAN_CALENDAR,
		},

		forPaint: [LazyAtlassianNavigation],
	})),
	createEntry(softwarePlanReleasesRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanReleasesPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
			advancedRoadmapsLastVisitedProgramBoardIdResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
			horizontal: AsyncHorizontalNavPlans,
			onlyShowHorizontalOnNav4: true,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanReleases],
	})),
	createEntry(softwarePlanTimelineRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanTimelinePage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
			advancedRoadmapsLastVisitedProgramBoardIdResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
			horizontal: AsyncHorizontalNavPlans,
			onlyShowHorizontalOnNav4: true,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanTimeline],
	})),
	createEntry(softwarePlanSettingsPermissionsRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsPermissionsPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsPermissions],
	})),
	createEntry(softwarePlanSettingsSavedViewsRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsSavedViewsPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsSavedViews],
	})),
	createEntry(softwarePlanSettingsPlanInfoRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsPlanInfoPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsPlanInfo],
	})),
	createEntry(softwarePlanSettingsProgramBoardsRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsProgramBoardsPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsProgramBoards],
	})),
	createEntry(softwarePlanSettingsSchedulingRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsSchedulingPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsScheduling],
	})),
	createEntry(softwarePlanSettingsScenariosRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsScenariosPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsScenarios],
	})),
	createEntry(softwarePlanSettingsAutoSchedulerRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsAutoSchedulerPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsAutoScheduler],
	})),
	createEntry(softwarePlanSettingsCustomFieldsRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsCustomFieldsPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsCustomFields],
	})),
	createEntry(softwarePlanSettingsExclusionRulesRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsExclusionRulesPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsExclusionRules],
	})),
	createEntry(softwarePlanSettingsIssueSourcesRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsIssueSourcesPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsIssueSources],
	})),
	createEntry(softwarePlanSettingsFindYourIssueRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsIssueFindYourIssuePage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsFindYourIssue],
	})),
	createEntry(softwarePlanSettingsRemovedIssuesRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanSettingsRemovedIssuesPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			sidebarId: SIDEBAR_ID.PLAN_SETTINGS,
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanSettingsRemovedIssues],
	})),
	createEntry(softwarePlanTeamsRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: PlanTeamsPage,
		layout: composeLayouts(globalLayout, planLayout),

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarPlanResources(),
			advancedRoadmapsPlanPageInitialChecksResource,
			advancedRoadmapsReduxStoreStateResource,
		],

		preloadOptions: {
			earlyChunks: ['advanced-roadmaps-early-entry'],
		},

		navigation: {
			menuId: MENU_ID.PLANS,
			sidebar: AsyncSidebarPlan,
			horizontal: AsyncHorizontalNavPlans,
			onlyShowHorizontalOnNav4: true,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanTeams],
	})),
	createEntry(softwareAdvancedRoadmapsGettingStartedRedirectRoute, () => ({
		group: ROUTE_GROUPS_SOFTWARE,
		component: () => <ScreenRedirect to="/jira/plans?create-sample-plan=true" />,
		isRedirect: true,
		layout: globalLayout,
		resources: [...getNavigationResources()],

		navigation: {
			menuId: MENU_ID.PLANS,
		},

		forPaint: [LazyAtlassianNavigation, LazyPlanTimeline],
	})),
];
